import { supabase } from '../lib/supabase';

/**
 * Fetch admin dashboard metrics
 */
export async function getAdminDashboardMetrics() {
  try {
    // Get total students count
    const { count: totalStudents, error: studentsError } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('role', 'student');

    if (studentsError) throw studentsError;

    // Get active plays count
    const { count: activePlays, error: playsError } = await supabase?.from('plays')?.select('*', { count: 'exact', head: true })?.eq('is_published', true);

    if (playsError) throw playsError;

    // Get quiz completion rate
    const { data: quizAttempts, error: quizError } = await supabase?.from('quiz_attempts')?.select('passed');

    if (quizError) throw quizError;

    const totalAttempts = quizAttempts?.length || 0;
    const passedAttempts = quizAttempts?.filter(a => a?.passed)?.length || 0;
    const completionRate = totalAttempts > 0 ? ((passedAttempts / totalAttempts) * 100)?.toFixed(1) : 0;

    // Get average session time from user progress
    const { data: progressData, error: progressError } = await supabase?.from('user_progress')?.select('total_time_spent_minutes');

    if (progressError) throw progressError;

    const totalMinutes = progressData?.reduce((sum, p) => sum + (p?.total_time_spent_minutes || 0), 0) || 0;
    const avgSessionTime = progressData?.length > 0 ? Math.round(totalMinutes / progressData?.length) : 0;

    return {
      totalStudents: totalStudents || 0,
      activePlays: activePlays || 0,
      completionRate: `${completionRate}%`,
      avgSessionTime: `${avgSessionTime}m`
    };
  } catch (error) {
    console.error('Error fetching admin dashboard metrics:', error);
    throw error;
  }
}

/**
 * Fetch recent activities
 */
export async function getRecentActivities() {
  try {
    // Get recent quiz completions
    const { data: quizAttempts, error: quizError } = await supabase?.from('quiz_attempts')?.select(`
        id,
        score,
        total_questions,
        completed_at,
        user_id,
        user_profiles!inner(full_name, email)
      `)?.order('completed_at', { ascending: false })?.limit(5);

    if (quizError) throw quizError;

    // Get recent badge earnings - manual join since no FK relationship exists
    const { data: badges, error: badgesError } = await supabase?.from('achievement_badges')?.select(`
        id,
        badge_name,
        earned_at,
        user_id
      `)?.order('earned_at', { ascending: false })?.limit(5);

    if (badgesError) throw badgesError;

    // Fetch user profiles separately for badges
    const badgeUserIds = badges?.map(b => b?.user_id)?.filter(Boolean) || [];
    const { data: badgeUsers, error: badgeUsersError } = await supabase?.from('user_profiles')?.select('id, full_name, email')?.in('id', badgeUserIds);

    if (badgeUsersError) throw badgeUsersError;

    // Create a map for quick lookup
    const userMap = new Map(badgeUsers?.map(u => [u?.id, u]) || []);

    // Get recent user registrations
    const { data: newUsers, error: usersError } = await supabase?.from('user_profiles')?.select('id, full_name, email, created_at')?.eq('role', 'student')?.order('created_at', { ascending: false })?.limit(5);

    if (usersError) throw usersError;

    // Combine and format activities
    const activities = [];

    quizAttempts?.forEach(attempt => {
      const percentage = attempt?.total_questions > 0 
        ? Math.round((attempt?.score / attempt?.total_questions) * 100) 
        : 0;
      activities?.push({
        id: `quiz-${attempt?.id}`,
        type: 'quiz_completed',
        userName: attempt?.user_profiles?.full_name || attempt?.user_profiles?.email || 'Unknown User',
        userAvatar: null,
        userAvatarAlt: `Avatar for ${attempt?.user_profiles?.full_name || 'user'}`,
        description: `completed a quiz with ${percentage}% score`,
        timestamp: new Date(attempt.completed_at)
      });
    });

    badges?.forEach(badge => {
      const userProfile = userMap?.get(badge?.user_id);
      activities?.push({
        id: `badge-${badge?.id}`,
        type: 'badge_earned',
        userName: userProfile?.full_name || userProfile?.email || 'Unknown User',
        userAvatar: null,
        userAvatarAlt: `Avatar for ${userProfile?.full_name || 'user'}`,
        description: `earned the '${badge?.badge_name}' badge`,
        timestamp: new Date(badge.earned_at)
      });
    });

    newUsers?.forEach(user => {
      activities?.push({
        id: `user-${user?.id}`,
        type: 'user_registered',
        userName: user?.full_name || user?.email || 'Unknown User',
        userAvatar: null,
        userAvatarAlt: `Avatar for ${user?.full_name || 'user'}`,
        description: 'registered as a new student',
        timestamp: new Date(user.created_at)
      });
    });

    // Sort by timestamp and return top 10
    return activities?.sort((a, b) => b?.timestamp - a?.timestamp)?.slice(0, 10);
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    throw error;
  }
}

/**
 * Fetch top performing students
 */
export async function getTopStudents() {
  try {
    const { data: students, error } = await supabase?.from('user_profiles')?.select(`
        id,
        full_name,
        email,
        xp_points,
        updated_at
      `)?.eq('role', 'student')?.order('xp_points', { ascending: false })?.limit(5);

    if (error) throw error;

    // Get quiz completion count for each student
    const studentsWithStats = await Promise.all(
      students?.map(async (student) => {
        const { count: quizCount } = await supabase?.from('quiz_attempts')?.select('*', { count: 'exact', head: true })?.eq('user_id', student?.id);

        const { count: badgeCount } = await supabase?.from('achievement_badges')?.select('*', { count: 'exact', head: true })?.eq('user_id', student?.id);

        // Calculate last active time
        const lastActiveDate = new Date(student.updated_at);
        const now = new Date();
        const diffMs = now - lastActiveDate;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        let lastActive = 'Just now';
        if (diffDays > 0) {
          lastActive = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        } else if (diffHours > 0) {
          lastActive = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        }

        return {
          id: student?.id,
          name: student?.full_name || student?.email,
          email: student?.email,
          avatar: null,
          avatarAlt: `Avatar for ${student?.full_name || student?.email}`,
          xp: student?.xp_points || 0,
          quizzesCompleted: quizCount || 0,
          badges: badgeCount || 0,
          lastActive
        };
      }) || []
    );

    return studentsWithStats;
  } catch (error) {
    console.error('Error fetching top students:', error);
    throw error;
  }
}

/**
 * Fetch system alerts
 */
export async function getSystemAlerts() {
  try {
    const alerts = [];

    // Check for missing audio files
    const { data: linesWithoutAudio, error: audioError } = await supabase?.from('lines')?.select(`
        id,
        play_id,
        plays!inner(title)
      `)?.eq('is_stage_direction', 'false')?.limit(1000);

    if (audioError) throw audioError;

    // Get lines that have audio
    const { data: linesWithAudio, error: audioCheckError } = await supabase?.from('audio_recordings')?.select('line_id');

    if (audioCheckError) throw audioCheckError;

    const audioLineIds = new Set(linesWithAudio?.map(a => a.line_id) || []);
    const missingAudioCount = linesWithoutAudio?.filter(line => !audioLineIds?.has(line?.id))?.length || 0;

    if (missingAudioCount > 0) {
      alerts?.push({
        id: 'audio-missing',
        type: 'warning',
        title: 'Audio Files Missing',
        message: `${missingAudioCount} lines need pronunciation audio uploads`,
        actionLabel: 'Upload Audio',
        actionPath: '/audio-file-management'
      });
    }

    // Check for glossary terms needed
    const { count: glossaryCount, error: glossaryError } = await supabase?.from('glossary_terms')?.select('*', { count: 'exact', head: true });

    if (glossaryError) throw glossaryError;

    if (glossaryCount === 0) {
      alerts?.push({
        id: 'glossary-empty',
        type: 'info',
        title: 'Glossary Review Needed',
        message: 'No glossary terms defined yet. Add definitions to help students.',
        actionLabel: 'Add Terms',
        actionPath: '/glossary-management'
      });
    }

    return alerts;
  } catch (error) {
    console.error('Error fetching system alerts:', error);
    throw error;
  }
}